<template>
	<v-card dark outlined class="stats-shift-projection-card pa-3" style="background-color:#1E1E1E; border-color:#2D2D2D;">
		<div class="d-flex align-center justify-space-between mb-2">
			<div class="caption grey--text text-uppercase" style="letter-spacing:0.05em;">
				<v-icon small left color="#FFA726">mdi-calendar-clock</v-icon>{{ $ml.get('alarm_stats_projection') }}
			</div>
		</div>

		<!-- Projected total — big number -->
		<div class="text-center mb-1">
			<div class="text-h3 font-weight-bold" style="color:#FFA726;">
				{{ formatNumber(projectedTotal) }}
			</div>
			<div class="caption grey--text mt-1">
				{{ $ml.get('alarm_stats_projection_desc') }}
			</div>
		</div>

		<!-- Shift progress bar -->
		<div class="mt-3">
			<div class="d-flex justify-space-between caption mb-1">
				<span class="grey--text">{{ $ml.get('alarm_stats_shift_progress') }}</span>
				<span style="color:#FFA726;">{{ shiftProgressPct }}%</span>
			</div>
			<v-progress-linear
				:value="shiftProgressPct"
				color="#FFA726"
				background-color="#2D2D2D"
				height="8"
				rounded
			></v-progress-linear>
		</div>

		<!-- Duration / remaining boxes -->
		<v-row no-gutters class="mt-3">
			<v-col cols="6" class="pr-1">
				<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
					<div class="caption grey--text text-uppercase" style="font-size:10px;">{{ $ml.get('alarm_stats_shift_duration') }}</div>
					<div class="subtitle-2 font-weight-bold white--text mt-1">
						{{ formatDuration(shiftDurationMs) }}
					</div>
				</v-card>
			</v-col>
			<v-col cols="6" class="pl-1">
				<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
					<div class="caption grey--text text-uppercase" style="font-size:10px;">{{ $ml.get('alarm_stats_time_remaining') }}</div>
					<div class="subtitle-2 font-weight-bold mt-1" style="color:#26C6DA;">
						{{ formatDuration(remainingMs) }}
					</div>
				</v-card>
			</v-col>
		</v-row>
	</v-card>
</template>

<script>
export default {
	name : 'StatsShiftProjection',

	props : {
		upm : {
			type    : Number,
			default : 0,
		},
		totalCounted : {
			type    : Number,
			default : 0,
		},
		shiftElapsedMs : {
			type    : Number,
			default : 0,
		},
		shiftDurationMs : {
			type    : Number,
			default : 32400000, // 9h default
		},
		shiftConfig : {
			type    : Object,
			default : () => ({ shift_start: '08:00', shift_duration_min: 540, target_rate: 0, stoppage_threshold_ms: 5000 }),
		},
	},

	computed : {
		effectiveDurationMs() {
			// Prefer explicit prop, fallback to shiftConfig
			// Supports both old 'duration_minutes' and new 'shift_duration_min' keys
			if (this.shiftDurationMs && this.shiftDurationMs > 0) {
				return this.shiftDurationMs;
			}
			const cfg = this.shiftConfig || {};
			const mins = cfg.shift_duration_min != null ? cfg.shift_duration_min
			           : (cfg.duration_minutes  != null ? cfg.duration_minutes  : 540);
			return mins * 60000;
		},

		remainingMs() {
			return Math.max(0, this.effectiveDurationMs - (this.shiftElapsedMs || 0));
		},

		remainingMin() {
			return this.remainingMs / 60000;
		},

		projectedTotal() {
			const upm     = this.upm || 0;
			const counted = this.totalCounted || 0;
			if (!upm) return counted; // avoid NaN when no rate
			return Math.round((upm * this.remainingMin) + counted);
		},

		shiftProgressPct() {
			const duration = this.effectiveDurationMs;
			if (!duration) return 0;
			return Math.min(100, Math.round(((this.shiftElapsedMs || 0) / duration) * 100));
		},
	},

	methods : {
		formatNumber(value) {
			if (value === null || value === undefined) return '0';
			return Number(value).toLocaleString();
		},

		formatDuration(ms) {
			if (!ms || ms <= 0) return '0m';
			const totalSeconds = Math.floor(ms / 1000);
			const hours        = Math.floor(totalSeconds / 3600);
			const minutes      = Math.floor((totalSeconds % 3600) / 60);
			if (hours > 0) return `${hours}h ${minutes}m`;
			return `${minutes}m`;
		},
	},
};
</script>

<style scoped>
.stats-shift-projection-card {
	min-height: 200px;
}
</style>
